import React, { useRef, useState, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid";

/**
 * Defers rendering of children until the placeholder scrolls into view.
 * Prevents Safari crashes caused by simultaneously mounting multiple
 * heavy chart components (e.g., Recharts ScatterCharts with large datasets).
 *
 * Once rendered, the component stays mounted to avoid re-render costs.
 */
export default function LazyRender({
  children,
  minHeight = 450,
}: {
  children: React.ReactNode;
  minHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect(); // only need to trigger once
        }
      },
      { rootMargin: "200px" } // start loading slightly before it's in view
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!visible) {
    return (
      <Grid item maxWidth="90vw" ref={ref}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: `${minHeight}px`,
            width: "500px",
            maxWidth: "90vw",
          }}
        >
          <CircularProgress />
        </Box>
      </Grid>
    );
  }

  return <>{children}</>;
}
